import { TextField, UIField, LabelFunction, StaticLabel, Field } from 'payload'
import { NEXT_PUBLIC_URL } from '@utils/constants'
import { FieldWithouType, addCellComponent } from '@utils/fields'
import { trim, uniqueId } from '@utils/misc'
import { flattenObjectArrayByKey, mergeObjects } from '@utils/objects'
import { slugify } from '@utils/slug'

/**
 * Creates a Text Field for title
 *
 * @param config
 * @returns
 */
export function createTitleField(label: LabelFunction | StaticLabel = 'Título', config?: Omit<FieldWithouType<CustomPayloadField<TextField>>, 'name'>): CustomPayloadField<TextField>[] {
  const configDefault: CustomPayloadField<TextField> = {
    type: 'text',
    name: 'title',
    label,
    required: true,
    admin: {
      hidden: true,
      className: 'read-only-text',
      readOnly: true,
    },
  };

  const field: CustomPayloadField<TextField> = mergeObjects(configDefault, config || {});
  return [ field ];
}

/**
 * Creates a Preview URL
 *
 * @param config
 * @returns
 */
export function createPreviewUrlField(path?: string, config?: FieldWithouType<CustomPayloadField<UIField>>, uniqueSlug: boolean = true): [ UIField, TextField, TextField, TextField ] {
  const configDefault: CustomPayloadField<UIField> = {
    name: 'preview-url',
    type: 'ui',
    label: 'Link',
    admin: {
      disableListColumn: true,
      condition: (data) => !! data?.id,
      components: {
        Field: '@components/(payload)/fields/ui/preview-link',
      }
    },
  };

  // UI
  const preview: UIField = mergeObjects(configDefault, config || {});

  // Virtual to be displayed on UI
  const permalink: TextField = {
    name: 'permalink',
    type: 'text',
    virtual: true,
    admin: {
      hidden: true,
    },
    hooks: {
      afterRead: [
        ({ collection, data }) => `${ NEXT_PUBLIC_URL }/${ path || collection?.slug }/${ ( data?.slug || uniqueId() ) }`,
      ]
    }
  };

  // Database Slug
  const originalSlug: TextField = {
    name: 'original_slug',
    type: 'text',
    label: 'URL',
    admin: {
      hidden: true,
    },
    hooks: {
      beforeChange: [
        async ({ value, data, originalDoc }) => {
          const title = data?.title ?? originalDoc?.title ?? value;
          return slugify(trim(title));
        }
      ]
    }
  };

  const slug: TextField = {
    name: 'slug',
    type: 'text',
    label: 'URL',
    defaultValue: () => uniqueId(),
    admin: {
      hidden: true,
    },
    hooks: {
      beforeChange: [
        async ({ collection, value, data, originalDoc, req }) => {
          if (! uniqueSlug || ! collection ) {
            return value;
          }

          const title = (data?.title ?? originalDoc?.title ?? value);
          const slug = slugify(trim(title));

          const currentId = originalDoc.id ?? -1;

          const where = { original_slug: { equals: slug }, id: { not_equals: currentId } };
          const existing = await req.payload.find({ collection: collection.slug, where, pagination: false });

          if (! existing.totalDocs) {
            return slug;
          }

          const existingSlugs = flattenObjectArrayByKey(existing.docs as { slug: string }[], 'slug');

          let maybeSlug = slug;
          let i = 1;

          while (existingSlugs.includes(maybeSlug)) {
            i++;
            maybeSlug = `${slug}-${i}`;
          }

          return maybeSlug;
        }
      ]
    }
  };

  return [ preview, permalink, originalSlug, slug ];
}

/**
 * Creates a PatternField (payload-better-fields-plugin) for CPF
 *
 * @param config
 * @param options
 * @returns
 */
export function emailField(config: Omit<CustomPayloadField<TextField>, 'type' | 'name'>): CustomPayloadField<TextField>[] {
  const configDefault: CustomPayloadField<TextField> = {
    name: 'email',
    type: 'text',
    label: 'E-mail',
    // validate: validateEmail,
  }

  const field: CustomPayloadField<TextField> = mergeObjects(configDefault, config);
  return [ field ];
}

/**
 * Add a permalink to cell value
 *
 * @param config
 * @returns
 */
export function addPermalinkToCell<T extends Field = Field>(field: Partial<T>): T[] {
  return addCellComponent(field, 'title-with-permalink');
}
